import { TextKey, RouteName } from '@mews-commander/enums';
import { useLocalizeText } from '@mews/framework';
import { UnicodeEntity } from '@mews/enums';
import {
    getCurrentEnterpriseServiceId,
    getCurrentEnterpriseChainId,
} from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { isCurrentEnterpriseCheckoutReportEnabled } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import {
    canCurrentProfileManageReservations,
    canCurrentProfileManageRates,
    canCurrentEmployeeUseTimeline,
} from '@mews-commander/legacy-entities/src/selectors/session/scope';
import { useCommanderSelector } from '@mews-commander/utils';
import { AssistantNavigation } from './AssistantNavigation';
import { useNavigationSearchResult } from './useNavigationSearchResult';
import { useNavigationPath } from './useNavigationPath';

export const useReservationsNavigation = (): AssistantNavigation[] => {
    const enterpriseId = useCommanderSelector<string>(getCurrentEnterpriseChainId);
    const serviceId = useCommanderSelector<string>(getCurrentEnterpriseServiceId);
    const canManageReservations = useCommanderSelector<boolean>(canCurrentProfileManageReservations);
    const canUseTimeline = useCommanderSelector((state) => canCurrentEmployeeUseTimeline(state));
    const canManageRates = useCommanderSelector<boolean>(canCurrentProfileManageRates);
    const checkoutReportEnabled = useCommanderSelector<boolean>(isCurrentEnterpriseCheckoutReportEnabled);
    const localizeText = useLocalizeText();

    const parentPath = `${localizeText({ textKey: TextKey.ReservationPlural })}${UnicodeEntity.Space}${
        UnicodeEntity.ForwardSlash
    }${UnicodeEntity.Space}`;

    const reservationCreate = useNavigationSearchResult(
        RouteName.ReservationCreate,
        TextKey.NewReservation,
        {
            query: { HotelId: enterpriseId },
        },
        useNavigationPath(parentPath, TextKey.NewReservation)
    );
    const reservationOverviewReport = useNavigationSearchResult(
        RouteName.ReservationOverviewReportIndex,
        TextKey.ReservationOverview,
        {
            query: { HotelId: enterpriseId },
        },
        useNavigationPath(parentPath, TextKey.ReservationOverview)
    );
    const reservationDashboard = useNavigationSearchResult(
        RouteName.ReservationDashboard,
        TextKey.TimeLine,
        {
            query: { ServiceId: serviceId },
        },
        useNavigationPath(parentPath, TextKey.TimeLine)
    );
    const reservationReport = useNavigationSearchResult(
        RouteName.ReservationReportIndex,
        TextKey.ReservationReport,
        {},
        useNavigationPath(parentPath, TextKey.ReservationReport)
    );
    const checkoutReport = useNavigationSearchResult(
        RouteName.CheckoutReportIndex,
        TextKey.CheckoutReport,
        {},
        useNavigationPath(parentPath, TextKey.CheckoutReport)
    );
    const availabilityBlockReport = useNavigationSearchResult(
        RouteName.AvailabilityBlockReportIndex,
        TextKey.AvailabilityBlockReport,
        {},
        useNavigationPath(parentPath, TextKey.AvailabilityBlockReport)
    );
    const availabilityReport = useNavigationSearchResult(
        RouteName.AvailabilityReportIndex,
        TextKey.AvailabilityOccupancyReport,
        {
            query: { PartialBind: 'true', ScopeId: enterpriseId },
        },
        useNavigationPath(parentPath, TextKey.AvailabilityOccupancyReport)
    );
    const visitDefaultPricing = useNavigationSearchResult(
        RouteName.VisitDefaultPricing,
        TextKey.RateManagement,
        {
            serviceId,
        },
        useNavigationPath(parentPath, TextKey.RateManagement)
    );

    if (!serviceId) {
        return [];
    }

    return [
        canManageReservations ? reservationCreate : null,
        reservationOverviewReport,
        !canUseTimeline ? null : reservationDashboard,
        reservationReport,
        checkoutReportEnabled ? checkoutReport : null,
        availabilityBlockReport,
        availabilityReport,
        canManageRates ? visitDefaultPricing : null,
    ].filter(Boolean) as AssistantNavigation[];
};
